import { Injectable } from '@angular/core';
import { Product, ProductDetail } from 'src/@omnial/_models/catalog/product.model';
import { Customer } from 'src/@omnial/_models/customer/customer.model';
import { Order } from 'src/@omnial/_models/order/order.model';
import { FaceBookCheckout, FaceBookContents, FaceBookConversion, FaceBookPayment, FaceBookProduct, FaceBookProductView, FaceBookPurchase } from 'src/@omnial/_models/tracking/facebook.events.model';
import { environment } from 'src/environments/environment';
import { CustomerService } from '../customer/customer.service';
import { CookieService } from 'ngx-cookie-service';
import { RepositoryService } from '../repository.service';
import { Subscription } from 'rxjs';
import { Router } from '@angular/router';

declare var fbq: any;

@Injectable()
export class FacebookService {

  private subscriptions: Subscription[] = [];

  constructor(private customerService: CustomerService,
    private cookieService: CookieService,
    public repoService: RepositoryService) { }

  public push(event: string, object?: any) {
    try {
      if (object) {
        if (environment.faceBookConversionApi) {
          // The conversions API will give us some dup-duplication info
          // We have to pass numerous things from the front end though
          const conversion = new FaceBookConversion();
          conversion.userAgent = window.navigator.userAgent;
          conversion.url = location.href;
          conversion.customerGuid = this.customerService.getCustomerGuid();
          conversion.conversionType = event;
          conversion.conversionData = object;
          conversion.fbc = this.cookieService.get('_fbc');
          conversion.fbp = this.cookieService.get('_fbp');
          this.subscriptions.push(this.repoService.create('FaceBook/Convert', conversion).subscribe({
            next: (res) => {
              if (res) {
                fbq('track', event, object, { eventID: res });
              } else {
                // Call fbq anyway without the Event ID
                fbq('track', event, object);
              }
            },
            error: (e) => {
              console.log('!!! Facebook Conversion API Error !!!!');
              console.log(e);
              // Call fbq anyway without the Event ID
              fbq('track', event, object);
            }
          }));
        } else {
          fbq('track', event, object);
        }
      } else {
        fbq('track', event);
      }
    } catch (error) {
      console.log('!!! Facebook Error !!!!');
      console.log(error);
      console.log(event);
    }
  }

  public pushPage() {
    this.push('PageView');
  }

  public pushAddPaymentInfo(customer: Customer) {
    const contentIds: string[] = [];
    const contents: FaceBookContents[] = [];
    customer?.cartList?.forEach((item) => {
      contentIds.push(item.sku);
      const contentItem = new FaceBookContents();
      contentItem.id = item.sku;
      contentItem.quantity = item.cartQuantity;
      contents.push(contentItem);
    });
    const paymentObject = new FaceBookPayment()
    paymentObject.content_ids = contentIds;
    paymentObject.contents = contents;
    paymentObject.currency = customer?.cartList?.length > 0 ? customer.cartList[0].currency : environment.currencyCode;
    paymentObject.value = customer?.orderTotals?.orderTotal;
    this.push('AddPaymentInfo', paymentObject);
  }

  public pushAddToCart(product: Product, quantity: number): void {
    const contentIds: string[] = [product?.sku];
    const contents: FaceBookContents[] = [];
    const contentItem = new FaceBookContents();
    contentItem.id = product.sku;
    contentItem.quantity = quantity;
    contents.push(contentItem);
    const cartObject = new FaceBookProduct();
    cartObject.content_name = product?.listName;
    cartObject.content_ids = contentIds;
    cartObject.contents = contents;
    cartObject.currency = product?.currency;
    cartObject.value = product.valuePrice;
    this.push('AddToCart', cartObject);
  }

  public pushAddToCartDetail(product: ProductDetail, quantity: number): void {
    const contentIds: string[] = [product?.sku];
    const contents: FaceBookContents[] = [];
    const contentItem = new FaceBookContents();
    contentItem.id = product.sku;
    contentItem.quantity = quantity;
    contents.push(contentItem);
    const cartObject = new FaceBookProduct();
    cartObject.content_name = product?.listName;
    cartObject.content_ids = contentIds;
    cartObject.contents = contents;
    cartObject.currency = product?.currency;
    cartObject.value = product?.valuePrice;
    this.push('AddToCart', cartObject);
  }

  public pushAddToWishList(product: Product): void {
    const contentIds: string[] = [product?.sku];
    const contents: FaceBookContents[] = [];
    const contentItem = new FaceBookContents();
    contentItem.id = product.sku;
    contentItem.quantity = 1;
    contents.push(contentItem);
    const wishListObject = new FaceBookProduct();
    wishListObject.content_name = product?.listName;
    wishListObject.content_ids = contentIds;
    wishListObject.contents = contents;
    wishListObject.currency = product?.currency;
    wishListObject.value = product?.valuePrice;
    this.push('AddToWishlist', wishListObject);
  }

  public pushBeginCheckout(customer: Customer): void {
    const contentIds: string[] = [];
    const contents: FaceBookContents[] = [];
    let noItems = 0;
    customer?.cartList?.forEach((item) => {
      contentIds.push(item.sku);
      const contentItem = new FaceBookContents();
      contentItem.id = item.sku;
      contentItem.quantity = item.cartQuantity;
      contents.push(contentItem);
      noItems += item.cartQuantity;
    });
    const checkoutObject = new FaceBookCheckout();
    checkoutObject.content_ids = contentIds;
    checkoutObject.contents = contents;
    checkoutObject.currency = customer?.cartList?.length > 0 ? customer.cartList[0].currency : environment.currencyCode;
    checkoutObject.num_items = noItems;
    checkoutObject.value = customer?.orderTotals?.orderTotal;
    this.push('InitiateCheckout', checkoutObject);
  }

  public pushGenerateLead(value: number, currencyCode: string): void {
    this.push('Lead', { content_category: 'omnial_lead', content_name: 'omnial_lead', currency: currencyCode, value: value });
  }

  public pushPurchase(order: Order): void {
    const contents: FaceBookContents[] = [];
    let noItems = 0;
    order?.items?.forEach((item) => {
      const contentItem = new FaceBookContents();
      contentItem.id = item?.product?.sku;
      contentItem.quantity = item.quantity;
      contents.push(contentItem);
      noItems += item.quantity;
    });
    const purchaseObject = new FaceBookPurchase();
    purchaseObject.contents = contents;
    purchaseObject.currency = order.customerCurrencyCode;
    purchaseObject.num_items = noItems;
    purchaseObject.value = order?.orderTotal;
    this.push('Purchase', purchaseObject);
  }

  public pushSearch(term: string): void {
    this.push('Search', { search_string: term });
  }

  public pushSignUp(): void {
    this.push('CompleteRegistration');
  }

  public pushViewItem(product: ProductDetail, listName: string, listId: string): void {
    const contents: FaceBookContents[] = [];
    const contentItem = new FaceBookContents();
    contentItem.id = product.sku;
    contentItem.quantity = 1;
    contents.push(contentItem);
    const productObject = new FaceBookProductView();
    productObject.content_name = listName;
    productObject.content_category = listId;
    productObject.contents = contents;
    productObject.currency = product?.currency;
    productObject.value = product.valuePrice;
    this.push('ViewContent', productObject);
  }
}
